import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useDetailedStorageUsage } from '@/hooks/useDetailedStorageUsage';
import { HardDrive, FileText, Scale, BookOpen, Calendar, Plus, Crown, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface StorageUsageDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const StorageUsageDetailsDialog: React.FC<StorageUsageDetailsDialogProps> = ({ open, onOpenChange }) => {
  const { data, isLoading } = useDetailedStorageUsage();
  const navigate = useNavigate();

  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getPlanDisplayName = (planType: string) => {
    const names: Record<string, string> = {
      free: 'Gratuito',
      basic: 'Básico',
      pro: 'Pro',
      premium: 'Premium'
    };
    return names[planType] || planType;
  };

  const getCategoryIcon = (iconName: string) => {
    switch (iconName) {
      case 'file-text':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'scale':
        return <Scale className="h-4 w-4 text-purple-500" />;
      case 'book-open':
        return <BookOpen className="h-4 w-4 text-green-500" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const handleBuyStorage = () => {
    onOpenChange(false);
    navigate('/planos');
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!data) {
    return null;
  }

  const usagePercentage = data.totalLimit > 0 ? (data.totalUsed / data.totalLimit) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <HardDrive className="h-5 w-5 text-primary" />
            Detalhes de Uso - Armazenamento
          </DialogTitle>
          <DialogDescription>
            Visualização detalhada do uso de armazenamento
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Plano Atual */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Crown className="h-4 w-4" />
              Plano Atual
            </div>
            <div className="rounded-lg bg-muted/50 p-3 space-y-1">
              <div className="font-semibold">{getPlanDisplayName(data.planType)}</div>
              {data.planExpiresAt && (
                <div className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Ativo até: {formatDate(data.planExpiresAt)}
                </div>
              )}
            </div>
          </div>

          {/* Uso por Categoria */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-muted-foreground">📁 Uso por Categoria</div>
            
            {data.categories.map((category, idx) => (
              <div key={idx} className="rounded-lg border bg-card p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(category.icon)}
                    <span className="text-sm font-medium">{category.name}</span>
                  </div>
                  <span className="text-sm font-semibold">{formatSize(category.size)}</span>
                </div>
                <Progress value={category.percentage} className="h-2" />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{category.fileCount} arquivo{category.fileCount !== 1 ? 's' : ''}</span>
                  <span>{category.percentage.toFixed(1)}% do total usado</span>
                </div>
              </div>
            ))}

            {/* Total */}
            <div className="rounded-lg bg-muted p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Total Usado</span>
                <span className="font-semibold">
                  {formatSize(data.totalUsed)} / {formatSize(data.totalLimit)}
                </span>
              </div>
              <Progress value={usagePercentage} className="h-2 mt-2" />
            </div>
          </div>

          {/* Armazenamento Extra */}
          {data.purchasedStorage.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <HardDrive className="h-4 w-4" />
                💎 Armazenamento Extra
              </div>
              <div className="space-y-2">
                {data.purchasedStorage.map((ps, idx) => (
                  <div key={idx} className="rounded-lg border bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20 p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        <div className="font-medium">+ {formatSize(ps.remaining)}</div>
                        <div className="text-xs text-muted-foreground">
                          de {formatSize(ps.amount)} originais
                        </div>
                      </div>
                      <div className="text-right text-xs">
                        <div className="text-muted-foreground">expira em</div>
                        <div className="font-semibold">{ps.daysUntilExpiry}d</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Botão de Compra */}
          <Button 
            variant="outline" 
            className="w-full border-primary/20 hover:bg-primary/5 hover:border-primary/30 transition-colors"
            onClick={handleBuyStorage}
          >
            <Plus className="w-4 h-4 mr-2" />
            Comprar Mais Armazenamento
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
